import { styled } from '@mui/material/styles';
import { COLOR, TRANSPARENT_COLOR } from '@src/styles/color';
import { createGlobalStyle } from 'styled-components';

export const StyledVideoPreviewPanel = styled('div')`
  background-color: ${COLOR.white};
  padding: 12px 16px 24px;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  justify-content: center;
  align-items: center;
`;

export const StyledScrollArea = styled('div')`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  overflow: hidden;
`;

export const StyledVideoStage = styled('div')`
  min-width: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

export const StyledFunctionBar = styled('div')`
  display: flex;
  align-items: center;
  gap: 0;
  border: 0.5px solid ${COLOR.secondary[16]};
  border-radius: 12px;
  padding: 0;
  flex-shrink: 0;
  width: fit-content;
  background-color: ${COLOR.white};
`;

export const StyledTabSection = styled('div')`
  display: flex;
  align-items: center;
  padding: 4px;
  box-sizing: border-box;
  ${({ hasDividerRight }) =>
    hasDividerRight ? `border-right: 0.5px solid ${COLOR.secondary[16]};` : ''}
  ${({ hasDividerLeft }) =>
    hasDividerLeft ? `border-left: 0.5px solid ${COLOR.secondary[16]};` : ''}
`;

export const StyledToggleButton = styled('button')`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  box-sizing: border-box;
  padding: 8px 12px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  background-color: ${({ isActive }) =>
    isActive ? COLOR.secondary[40] : 'transparent'};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #1c1c1e;
  flex-shrink: 0;

  .toggle-icon {
    width: 20px;
    height: 20px;
    font-size: 20px;
  }
`;

export const StyledIconOnlyButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  box-sizing: border-box;
  padding: 10px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  background-color: transparent;
  color: ${COLOR.text};
  flex-shrink: 0;

  &:hover {
    background-color: ${TRANSPARENT_COLOR.dark};
  }

  .toggle-icon {
    width: 20px;
    height: 20px;
    font-size: 20px;
  }
`;

export const StyledVideoFrame = styled('div')`
  ${({ aspectRatioValue }) =>
    aspectRatioValue ? `aspect-ratio: ${aspectRatioValue};` : ''}
  /* The caption layer anchors to the frame box — the same area the export
     renders — so with the overlay on, the no-ratio case needs a real box
     instead of display: contents. */
  display: ${({ aspectRatioValue, hasOverlay }) => {
    if (aspectRatioValue) return 'flex';
    return hasOverlay ? 'inline-flex' : 'contents';
  }};
  ${({ hasOverlay }) => (hasOverlay ? 'position: relative;' : '')}
  align-items: center;
  justify-content: center;
  max-width: 100%;
  max-height: 100%;
  min-height: 0;
  min-width: 0;
  width: auto;
  border-radius: 12px;
  overflow: hidden;
  box-sizing: border-box;
  background-color: ${({ showBackground, backgroundColor }) =>
    showBackground ? backgroundColor : 'transparent'};
`;

export const StyledVideo = styled('video')`
  display: block;
  width: ${({ hasFrame }) => (hasFrame ? '100%' : 'auto')};
  height: ${({ hasFrame }) => (hasFrame ? '100%' : 'auto')};
  max-width: 100%;
  max-height: 100%;
  min-height: 0;
  min-width: 0;
  object-fit: contain;
  background-color: transparent;
  /* Past the end of the video: hidden, so the frame's background colour shows
     through instead of the frozen final frame. Kept mounted (not unmounted) so
     playback position and buffering survive the crossing. */
  ${({ isHidden }) => (isHidden ? 'visibility: hidden;' : '')}
  ${({ hasFrame }) => (hasFrame ? '' : 'border-radius: 12px;')}
  box-sizing: border-box;
`;

export const StyledRatioDropdown = styled('div')`
  display: inline-flex;
`;

export const StyledBackgroundColorPicker = styled('div')`
  display: inline-flex;
`;

export const StyledBackgroundColorTrigger = styled('label')`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 8px;
  outline: none;
  border: none;
  cursor: pointer;
  background-color: transparent;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${COLOR.text};
  flex-shrink: 0;

  &:hover {
    background-color: ${({ isDisabled }) =>
      isDisabled ? 'transparent' : TRANSPARENT_COLOR.dark};
  }

  /* Frozen while the mp4 render runs (the <input> carries the real disabled). */
  ${({ isDisabled }) =>
    isDisabled &&
    `
    cursor: not-allowed;
    opacity: 0.5;
  `}

  .background-color-dot {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    box-shadow: inset 0 0 0 1px ${COLOR.black[16]};
    flex-shrink: 0;
  }

  .background-color-label {
    white-space: nowrap;
  }

  .background-color-input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: none;
    cursor: pointer;
    opacity: 0;

    /* The input covers the whole label, so its own cursor is the one the pointer
       lands on — the label's not-allowed never shows without this. */
    &:disabled {
      cursor: not-allowed;
    }
  }
`;

export const StyledTriggerButton = styled('button')`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 40px;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 8px;
  outline: none;
  border: none;
  cursor: pointer;
  background-color: ${({ isActive }) =>
    isActive ? COLOR.secondary[80] : 'transparent'};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${COLOR.text};
  flex-shrink: 0;

  &:hover {
    background-color: ${({ isActive }) =>
      isActive ? COLOR.secondary[80] : TRANSPARENT_COLOR.dark};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background-color: transparent;
  }

  .ratio-icon {
    width: 20px;
    height: 20px;
    font-size: 20px;
    flex-shrink: 0;
  }

  .ratio-label {
    flex: 1;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const StyledRatioMenu = styled('div')`
  border-radius: 8px;
  box-shadow: 0px 4px 16px 0px ${COLOR.boxShadow};
  padding: 8px;
  box-sizing: border-box;
  width: 305px;
  background-color: ${COLOR.white};
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const StyledRatioMenuItem = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 8px 12px;
  box-sizing: border-box;
  border-radius: 8px;
  cursor: pointer;
  color: ${COLOR.text};
  background-color: ${({ isSelected }) =>
    isSelected ? COLOR.secondary[80] : 'transparent'};

  &:hover {
    color: ${COLOR.text};
    background-color: ${({ isSelected }) =>
      isSelected ? COLOR.secondary[80] : TRANSPARENT_COLOR.dark};
  }

  .ratio-item-icon {
    width: 24px;
    height: 24px;
    font-size: 24px;
    flex-shrink: 0;
  }

  .ratio-item-label {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: ${COLOR.text};
  }

  .ratio-item-description {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: rgba(28, 28, 30, 0.64);
    flex: 1;
    text-align: left;
  }

  .ratio-item-check {
    width: 24px;
    height: 24px;
    font-size: 20px;
    color: ${COLOR.text};
    flex-shrink: 0;
  }
`;

export const RatioTooltipGlobalStyle = createGlobalStyle`
  .rc-tooltip.aspect-ratio-tooltip {
    opacity: 1 !important;
  }

  .rc-tooltip.aspect-ratio-tooltip.rc-tooltip-placement-bottom,
  .rc-tooltip.aspect-ratio-tooltip.rc-tooltip-placement-bottomLeft,
  .rc-tooltip.aspect-ratio-tooltip.rc-tooltip-placement-bottomRight {
    padding: 8px 0 0 0 !important;
  }

  .rc-tooltip.aspect-ratio-tooltip.rc-tooltip-placement-top,
  .rc-tooltip.aspect-ratio-tooltip.rc-tooltip-placement-topLeft,
  .rc-tooltip.aspect-ratio-tooltip.rc-tooltip-placement-topRight {
    padding: 0 0 8px 0 !important;
  }

  .rc-tooltip.aspect-ratio-tooltip .rc-tooltip-content,
  .rc-tooltip.aspect-ratio-tooltip .rc-tooltip-inner {
    overflow: visible !important;
  }

  .rc-tooltip.aspect-ratio-tooltip .rc-tooltip-inner {
    padding: 0 !important;
    background-color: transparent !important;
    box-shadow: none !important;
    min-height: auto !important;
  }

  .rc-tooltip.aspect-ratio-tooltip .rc-tooltip-arrow {
    display: none !important;
  }
`;

// Transparent caption layer over the frame: same drawing engine as the export
// (video-renderer /draw), and it never redraws the video itself.
export const StyledCaptionOverlayCanvas = styled('canvas')`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;
